import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { Box, Button, lighten, Stack } from '@mui/material';
import { calculateAverage, calculateSum } from '@/utils/calculateTotalTable';

const TableOccupancyPanel = ({ data, setOpenModalClass, setRowSelected }) => {
  const columns = [
    {
      accessorKey: 'series',
      header: 'Série',
      size: 10,
      Cell: ({ renderedCellValue }) => (
        <Box
          sx={{
            borderRadius: '20px',
            background: '#A56EA533',
            textAlign: 'center',
            color: '#2C2C2C',
            fontWeight: 400,
            padding: '2px',
            maxWidth: '100px'
          }}
        >
          {renderedCellValue}
        </Box>
      ),
    },
    {
      accessorKey: 'shift',
      header: 'Turno',
      size:10,
      Cell: ({ renderedCellValue }) => (
        <Box
          sx={{
            borderRadius: '20px',
            background: '#D1E3FE',
            textAlign: 'center',
            color: '#2C2C2C',
            fontWeight: 400,
            padding: '2px',
          }}
        >
          {renderedCellValue}
        </Box>
      ),
    },
    {
      accessorKey: 'class',
      header: 'Turma',
      size: 10,
      Cell: ({ row }) => {
        const renderedCellValue = row.original.class
        return renderedCellValue && (
          <Stack flexDirection={'row'} justifyContent={'center'}>
            <Box
              sx={{
                borderRadius: '50%',
                background: '#FC9F9F',
                width: '30px',
                height: '28px',
                textAlign: 'center',
                paddingTop: '5px',
                color: '#2C2C2C',
                fontWeight: 400,
              }}
              title={renderedCellValue}
            >
              {renderedCellValue ? renderedCellValue.charAt(0) : ''}
            </Box>
          </Stack>
        );
      },
      Footer: () => (
        <Stack>
          <Box>Total</Box>
        </Stack>
      ),
    },
    {
      accessorKey: 'total',
      header: 'Total de vagas',
      size: 10,
      Cell: ({ renderedCellValue }) => (
        <Box
          sx={{
            fontSize: '16px',
            color: '#222222',
            fontWeight: 500,
          }}
        >
          {renderedCellValue}
        </Box>
      ),
      Footer: () => (
        <Stack>
          <Box>{calculateSum(data, 'total')}</Box>
        </Stack>
      ),
    },
    {
      accessorKey: 'reEnrollment',
      header: 'Rematrícula (Veteranos)',
      size: 10,
      Cell: ({ renderedCellValue }) => (
        <Box
          sx={{
            fontSize: '19px',
            color: '#222222',
            fontWeight: 500,
          }}
        >
          {renderedCellValue}
        </Box>
      ),
      Footer: () => (
        <Stack>
          <Box>{calculateSum(data, 'reEnrollment')}</Box>
        </Stack>
      ),
    },
    {
      accessorKey: 'newStudents',
      header: 'Matrícula (Novatos)',
      size: 10,
      Cell: ({ renderedCellValue }) => (
        <Box
          sx={{
            fontSize: '19px',
            color: '#222222',
            fontWeight: 500,
          }}
        >
          {renderedCellValue}
        </Box>
      ),
      Footer: () => (
        <Stack>
          <Box>{calculateSum(data, 'newStudents')}</Box>
        </Stack>
      ),
    },
    {
      accessorKey: 'totalEnrollment',
      header: 'Total de Matrículas',
      size: 10,
      Cell: ({ renderedCellValue }) => (
        <Box
          sx={{
            fontSize: '19px',
            color: '#222222',
            fontWeight: 500,
          }}
        >
          {renderedCellValue}
        </Box>
      ),
      Footer: () => (
        <Stack>
          <Box>{calculateSum(data, 'totalEnrollment')}</Box>
        </Stack>
      ),
    },
    {
      accessorKey: 'totalSupply',
      header: 'Total de Propostas',
      size: 10,
      Cell: ({ renderedCellValue }) => (
        <Box
          sx={{
            fontSize: '19px',
            color: '#222222',
            fontWeight: 500,
          }}
        >
          {renderedCellValue}
        </Box>
      ),
      Footer: () => (
        <Stack>
          <Box>{calculateSum(data, 'totalSupply')}</Box>
        </Stack>
      ),
    },

    {
      accessorKey: 'vacancies',
      header: 'Vagas',
      size: 10,
      Cell: ({ renderedCellValue }) => (
        <Box
          sx={{
            fontSize: '19px',
            color: '#222222',
            fontWeight: 500,
            borderRadius: '20px',
            background: renderedCellValue > 3 ? '#CEFACE' : renderedCellValue < 3 ? '#FCE4D4' : '#FEF8CB',
            padding: '5px',
            width: '80px',
            textAlign: 'center',
          }}
        >
          {renderedCellValue}
        </Box>
      ),
      Footer: () => (
        <Stack>
          <Box>{calculateSum(data, 'vacancies')}</Box>
        </Stack>
      ),
    },
    {
      accessorKey: 'occupancyPercentage',
      header: '% de ocupação',
      size: 10,
      Cell: ({ renderedCellValue }) => (
        <Box
          sx={{
            fontSize: '19px',
            color: '#222222',
            fontWeight: 500,
          }}
        >
          {`${renderedCellValue?.toFixed(2) || '0'}%`}
        </Box>
      ),
      Footer: () => (
        <Stack>
          <Box>{calculateAverage(data, 'occupancyPercentage')}</Box>
        </Stack>
      ),
    },
  ];

  const tableBasic = useMaterialReactTable({
    columns,
    data,
    enablePagination: false,
    enableSorting: true,
    enableRowSelection: false,
    enableGrouping: false,
    enableColumnFilters: false,
    positionActionsColumn: 'last',
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableExpandAll: false,
    enableRowDragging: false,
    enableColumnActions: true,
    enableBottomToolbar: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableFilters: false,
    enableGlobalFilter: true,
    positionToolbarAlertBanner: 'bottom',
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        '.Mui-TableHeadCell-Content-Wrapper': {
          whiteSpace: 'wrap',
          // minWidth: '70px !important',
        },
        color: 'black',
        fontWeight: 700,
        '.Mui-TableHeadCell-Content': { justifyContent: 'center', alignItems: 'center' },
        fontSize: '11px',
      },
    }),
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        setOpenModalClass(true);
        setRowSelected(row);
      },
      sx: {
        cursor: 'pointer',
      },
    }),
    muiTableFooterCellProps: ({ column }) => ({
      sx: {
        backgroundColor: '#CEFACE',
        fontSize: '15px',
        fontWeight: 500,
        textTransform: 'uppercase',
        color: '#000',
        textAlign: 'center',
      },
    }),
    muiTableBodyCellProps: ({ row }) => ({
      sx: {
        backgroundColor: '#fff',
        color: '#333333',
        fontWeight: 400,
        fontSize: '12px',
        textAlign: 'center',
      },
    }),
    muiTableContainerProps: () => ({
      className: 'hidden-scroll',
      sx: {
        maxHeight: '500px',
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '8px', 
          height: '4px', 
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#888', 
          borderRadius: '10px', 
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: '#555', 
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1', 
          borderRadius: '10px',
        },
      },
    }),
  });

  return (
    <>
      <MaterialReactTable table={tableBasic} />
    </>
  );
};

export default TableOccupancyPanel;
